import { create } from "zustand"

import {
  GetGoodsReceiptPalletsListDocument,
  type GetGoodsReceiptPalletsListQuery,
  type GoodsReceiptPallet,
  type GoodsReceiptPalletSortField,
  GoodsReceiptStatus,
  type PageInfo,
  type SortingOrder,
} from "@/graphql/codegen/graphql"
import { type SearchFilters, type SearchStore, createSearchStore } from "@/stores/createSearchStore"

export type StoredGoodsReceiptPalletsSearchFilters = SearchFilters & {
  warehouseAreaIds?: string[]
  field?: GoodsReceiptPalletSortField
  order?: SortingOrder
}

type ExtendedStoredGoodsReceiptPalletsSearchStore = SearchStore<
  GoodsReceiptPallet,
  StoredGoodsReceiptPalletsSearchFilters
> & {
  pageInfo?: PageInfo
}

const deduplicateGoodsReceiptPallets = (
  newData: GoodsReceiptPallet[],
  existingData: GoodsReceiptPallet[]
): GoodsReceiptPallet[] => {
  const uniqueIds = new Set(existingData.map((pallet) => pallet.goodsReceiptPalletId))
  return [...existingData, ...newData.filter((pallet) => !uniqueIds.has(pallet.goodsReceiptPalletId))]
}

export const useStoredGoodsReceiptPalletsSearchStore = create<ExtendedStoredGoodsReceiptPalletsSearchStore>(
  (set, get, store) => {
    const baseStoreCreator = createSearchStore<
      GoodsReceiptPallet,
      StoredGoodsReceiptPalletsSearchFilters,
      GetGoodsReceiptPalletsListQuery
    >({
      documentNode: GetGoodsReceiptPalletsListDocument,
      deduplicateData: deduplicateGoodsReceiptPallets,
      getQueryInput: (filters: StoredGoodsReceiptPalletsSearchFilters, offset: number, limit: number) => {
        const { field, order, warehouseAreaIds, ...otherFilters } = filters
        return {
          filter: {
            ...otherFilters,
            warehouseIds: warehouseAreaIds,
            status: GoodsReceiptStatus.Stored,
          },
          offset,
          limit,
          sort: field && order ? { field, order } : undefined,
        }
      },
      getQueryVariables: (queryInput) => ({ input: queryInput }),
      extractDataFromResponse: (data) => {
        const pageInfo =
          data.goodsReceipt.palletList.__typename === "GoodsReceiptPalletListSuccess"
            ? data.goodsReceipt.palletList.pageInfo
            : undefined

        set({ pageInfo })

        return {
          __typename: data.goodsReceipt.palletList.__typename,
          data:
            data.goodsReceipt.palletList.__typename === "GoodsReceiptPalletListSuccess"
              ? (data.goodsReceipt.palletList.goodsReceiptPallets as GoodsReceiptPallet[])
              : [],
          error:
            data.goodsReceipt.palletList.__typename === "GoodsReceiptPalletListFailure"
              ? data.goodsReceipt.palletList.error
              : undefined,
        }
      },
    })

    const baseStore = baseStoreCreator(set, get, store)

    return {
      ...baseStore,
      pageInfo: undefined,
    }
  }
)
